/**
 * Presenter-facing "demo mode" for the Connections sidebar.
 *
 * Demo mode forces every client-evaluated feature flag off (see
 * `SURVIVES_DEMO_MODE` in `useFeatureFlag`) so nothing flag-gated is on screen
 * while a clinician audience is watching — nav entries, in-page surfaces and
 * flag-guarded routes alike. It is deliberately a blunt instrument: the default
 * for a new flag is "hidden during a demo", so nobody has to remember to opt in.
 *
 * This hook owns eligibility and the toggle; the suppression state itself lives
 * in `utils/demo-mode-store` so `useFeatureFlag` can read it from anywhere and
 * from the first paint.
 *
 * @agentic connections
 */

import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useCurrentUser } from "@/app/auth/CurrentUserContext";
import { useFeatureFlag } from "@/hooks/shared/useFeatureFlag";
import {
  isDemoModeActive,
  setDemoModeActive,
  subscribeToDemoMode,
} from "@/utils/demo-mode-store";

/**
 * Demo-mode availability and state for the sidebar chrome.
 * @agentic connections
 */
export type DemoMode = {
  /** True when the current user may use demo mode (flag on, ICT, roles resolved). */
  available: boolean;
  /** True while demo mode is suppressing feature-flagged surfaces. */
  enabled: boolean;
  /** Flips demo mode. */
  toggle: () => void;
};

/**
 * Resolves demo-mode availability and current state.
 * @agentic connections
 */
export const useDemoMode = (): DemoMode => {
  const flagEnabled = useFeatureFlag("demoMode");
  const { isIctProfessional, isLoading, isRolesResolved } = useCurrentUser();
  const enabled = useSyncExternalStore(
    subscribeToDemoMode,
    isDemoModeActive,
    isDemoModeActive,
  );

  const rolesSettled = !isLoading && isRolesResolved;
  const available = flagEnabled && rolesSettled && isIctProfessional;

  // Suppression starts from the persisted preference at first paint, before we
  // know who the user is — otherwise a flagged surface flashes on screen during
  // role resolution, which is the whole thing demo mode exists to prevent. Once
  // roles settle, anyone who turns out to be ineligible has the state cleared,
  // so a stale preference can never leave a user with features suppressed and
  // no toggle to undo it.
  useEffect(() => {
    if (enabled && rolesSettled && !available) {
      setDemoModeActive(false);
    }
  }, [available, enabled, rolesSettled]);

  const toggle = useCallback(() => {
    setDemoModeActive(!isDemoModeActive());
  }, []);

  return { available, enabled, toggle };
};
